import type { ActivityPacket, CursorPacket, PresenceMember, PresenceView } from "@livcg/schema";
import { useCallback, useEffect, useRef, useState } from "react";
import { type PresenceClient, presenceSocket } from "../../shared/presenceSocket.js";
import { createCursorStore } from "./cursorStore.js";

const CURSOR_MS = 50;
const ACTIVITY_MS = 8000;

export type ReceivedActivity = ActivityPacket & { at: number };

export function usePresence(slug: string | null, view: PresenceView) {
  const [members, setMembers] = useState<PresenceMember[]>([]);
  const [cursors] = useState(createCursorStore);
  const [activity, setActivity] = useState<ReceivedActivity[]>([]);
  const [selfId, setSelfId] = useState<string | null>(null);
  const sock = useRef<PresenceClient | null>(null);
  const viewRef = useRef(view);
  viewRef.current = view;
  const lastSent = useRef(0);
  const trailing = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!slug) return;
    const s = presenceSocket();
    if (!s) return;
    sock.current = s;
    const join = () => {
      setSelfId(s.id ?? null);
      s.emit("join", { slug, view: viewRef.current });
    };
    const onRoster = (r: PresenceMember[]) => {
      setMembers(r);
      cursors.retain(new Set(r.map((m) => m.id)));
    };
    const onCursor = (c: CursorPacket) => cursors.set(c);
    const onActivity = (a: ActivityPacket) => {
      const now = Date.now();
      setActivity((cur) => [...cur.filter((x) => now - x.at < ACTIVITY_MS), { ...a, at: now }]);
    };
    s.on("connect", join);
    s.on("roster", onRoster);
    s.on("cursor", onCursor);
    s.on("activity", onActivity);
    if (s.connected) join();
    else s.connect();
    return () => {
      s.emit("leave");
      s.off("connect", join);
      s.off("roster", onRoster);
      s.off("cursor", onCursor);
      s.off("activity", onActivity);
      if (trailing.current) clearTimeout(trailing.current);
      trailing.current = null;
      sock.current = null;
      setMembers([]);
      cursors.clear();
      setActivity([]);
    };
  }, [slug, cursors]);

  useEffect(() => {
    if (sock.current?.connected) sock.current.emit("view", { view });
  }, [view]);

  const sendCursor = useCallback((p: { anchor: string | null; x: number; y: number }) => {
    const emit = (q: typeof p) => {
      lastSent.current = Date.now();
      if (sock.current?.connected) sock.current.emit("cursor", q);
    };
    if (trailing.current) {
      clearTimeout(trailing.current);
      trailing.current = null;
    }
    const wait = CURSOR_MS - (Date.now() - lastSent.current);
    if (p.anchor === null || wait <= 0) return emit(p);
    trailing.current = setTimeout(() => {
      trailing.current = null;
      emit(p);
    }, wait);
  }, []);

  return { members, cursors, activity, selfId, sendCursor };
}
